import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { apiGet } from "@/lib/api";
import { CheckoutWizard } from "@/components/assets/checkout-wizard";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useGoBack } from "@/hooks/use-go-back";

export const Route = createFileRoute(
  "/_authenticated/assets/$assetId/checkout"
)({
  component: CheckoutPage,
});

interface Asset {
  id: string;
  name: string;
  assetType: string;
  serialNumber: string | null;
  tagNumber: string | null;
  location: string | null;
  status: string;
  inspectionTemplateId: string | null;
  inspectionTemplateName: string | null;
  imageFileId: string | null;
  lastInspectedAt: string | null;
  nextInspectionDueAt: string | null;
  notes: string | null;
}

interface InspectionTemplate {
  id: string;
  name: string;
  category: string;
  isActive: boolean;
}

interface TemplateItem {
  id: string;
  label: string;
  description: string | null;
  sortOrder: number;
  isRequired: boolean;
}

function CheckoutPage() {
  const { assetId } = Route.useParams();
  const navigate = useNavigate();
  const goBackAssets = useGoBack({ to: "/assets" });
  const goBackAsset = useGoBack({ to: "/assets/$assetId", params: { assetId } });
  const queryClient = useQueryClient();

  // Fetch asset detail
  const { data: assetData, isLoading: assetLoading } = useQuery({
    queryKey: ["asset", assetId],
    queryFn: () => apiGet<{ data: Asset }>(`/api/assets/${assetId}`),
  });

  const asset = assetData?.data;

  // Fetch inspection template if linked
  const { data: templateData, isLoading: templateLoading } = useQuery({
    queryKey: ["inspection-template", asset?.inspectionTemplateId],
    queryFn: () =>
      apiGet<{ data: InspectionTemplate }>(
        `/api/admin/inspection-templates/${asset!.inspectionTemplateId}`
      ),
    enabled: !!asset?.inspectionTemplateId,
  });

  // Fetch template items
  const { data: itemsData, isLoading: itemsLoading } = useQuery({
    queryKey: ["inspection-template-items", asset?.inspectionTemplateId],
    queryFn: () =>
      apiGet<{ data: TemplateItem[] }>(
        `/api/admin/inspection-templates/${asset!.inspectionTemplateId}/items`
      ),
    enabled: !!asset?.inspectionTemplateId,
  });

  const isLoading = assetLoading || templateLoading || itemsLoading;

  const handleComplete = () => {
    toast.success("Asset checked out successfully!");
    queryClient.invalidateQueries({ queryKey: ["asset", assetId] });
    queryClient.invalidateQueries({ queryKey: ["my-checkouts"] });
    queryClient.invalidateQueries({ queryKey: ["assets"] });
    navigate({ to: "/assets/$assetId", params: { assetId } });
  };

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-lg mx-auto pt-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-10 w-full rounded-xl" />
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="space-y-4 max-w-lg mx-auto pt-4">
        <Button variant="ghost" size="sm" className="gap-1" onClick={goBackAssets}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Asset not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const inspectionTemplate =
    templateData?.data && templateData.data.isActive
      ? templateData.data
      : null;

  const templateItems = (itemsData?.data || []).sort(
    (a, b) => a.sortOrder - b.sortOrder
  );

  return (
    <div className="space-y-4 max-w-lg mx-auto pt-2">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" className="gap-1" onClick={goBackAsset}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>

      <CheckoutWizard
        asset={asset}
        inspectionTemplate={inspectionTemplate}
        templateItems={templateItems}
        onComplete={handleComplete}
      />
    </div>
  );
}
